<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
// "Histórico de cliente" — vista 1:1 com /history real Lais
// Usa LaisHeader + LaisCard + LaisInfoRow + LaisLinkButton primitivos
import {
  ref,
  computed,
  onBeforeUnmount,
  onMounted,
  watch,
  defineAsyncComponent,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useEscClose } from 'dashboard/composables/useEscClose';
import { resolveContactOrigin } from 'dashboard/helper/lais/originHelpers';
import LeadHistoryAPI from 'dashboard/api/leadHistory';
import BlockedContactsAPI from 'dashboard/api/blockedContacts';
import OnlineDutyAPI from 'dashboard/api/onlineDuty';
import {
  getPermissionTokensForAccount,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisGalleryTabBar from 'dashboard/components-next/LaisGalleryTabBar.vue';
import LeadJourney from '../components/LeadJourney.vue';
import LeadStageTimelinePanel from '../components/LeadStageTimelinePanel.vue';
import LeadContactCards from '../components/LeadContactCards.vue';
import ContactNotificationPreferences from '../components/ContactNotificationPreferences.vue';
import LeadDemandContextPanel from '../components/LeadDemandContextPanel.vue';
import LeadConversationPanel from '../components/LeadConversationPanel.vue';
import LeadHistoryHeaderActions from '../components/LeadHistoryHeaderActions.vue';
import LeadHistoryState from '../components/LeadHistoryState.vue';
import LeadBlockedBanner from '../components/LeadBlockedBanner.vue';
import LeadProfileErrorCard from '../components/LeadProfileErrorCard.vue';
import LeadProfileIdentityCard from '../components/LeadProfileIdentityCard.vue';
import LeadProfileSummaryCard from '../components/LeadProfileSummaryCard.vue';
import LeadPublicVisitRequestCard from '../components/LeadPublicVisitRequestCard.vue';
import LeadCrmSummaryCard from '../components/LeadCrmSummaryCard.vue';
import LeadPendingSupportCard from '../components/LeadPendingSupportCard.vue';
import LeadFeedbackCard from '../components/LeadFeedbackCard.vue';
import LeadBuyerProfileCard from '../components/LeadBuyerProfileCard.vue';
import LeadRecommendationsCard from '../components/LeadRecommendationsCard.vue';
import LeadHistoryTimelineCard from '../components/LeadHistoryTimelineCard.vue';
import LeadOperationAreasCard from '../components/LeadOperationAreasCard.vue';
import LeadNotesCard from '../components/LeadNotesCard.vue';
import LeadScheduledVisitsCard from '../components/LeadScheduledVisitsCard.vue';
import LeadOriginDrawer from '../components/LeadOriginDrawer.vue';
import LeadFeedbackDialogs from '../components/LeadFeedbackDialogs.vue';
import LeadNoteDialogs from '../components/LeadNoteDialogs.vue';
import LeadBlockDialogs from '../components/LeadBlockDialogs.vue';
import { trackCrmEvent } from '../utils/crmTracking';
import { activeBusinessContextFromLead } from '../utils/visitorInfoPresenter';
import { useLeadHistoryData } from '../composables/useLeadHistoryData';
import {
  FEEDBACK_SUCCESS_MESSAGE,
  HISTORY_TABS,
  HISTORY_TABS_ID,
  OPERATIONAL_TIMELINE_TYPES,
  SUPPORT_UPDATE_PERMISSIONS,
  TIMELINE_TYPE_META,
} from '../utils/leadHistoryConstants';
import {
  blockedContactPayload,
  compactQuery,
  formatDateCompact,
  normalizeSupportOrigin,
  normalizeSupportQuestion,
  pendingSupportPayloadFromResponse,
  qualificationAnswerText,
  qualificationQuestionItemsFromLead,
  querySignature,
  routeString,
  supportNextActionLabel,
  supportStatus,
  supportStatusMeta,
} from '../utils/leadHistoryPresenter';

const AlertsDrawer = defineAsyncComponent(
  () => import('dashboard/components-next/AlertsDrawer.vue')
);

const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const currentAccount = useMapGetter('getCurrentAccount');
// Reatribuída abaixo: o composable avisa a página sempre que um payload de
// histórico é aplicado, para regravar os rascunhos das respostas qualificatórias.
let prepareQualificationAnswerDrafts = () => {};

const {
  lead,
  conversations,
  visitas,
  crmSummary,
  buyerProfile,
  offers,
  favorites,
  recommendations,
  timeline,
  latestPendingSupport,
  publicVisitRequest,
  historyOriginPayload,
  crmOperations,
  selectedConversationPayload,
  conversationFeedback,
  selectedConversationId,
  messages,
  contactNotes,
  isLoading,
  isLoadingMessages,
  isLoadingConversationHistory,
  isLoadingNotes,
  profileLoadError,
  conversationLoadError,
  visitasLoadError,
  pendingSupportNextStatus,
  profileErrorCopy,
  historyErrorCopy,
  conversationErrorCopy,
  visitasErrorCopy,
  notesErrorCopy,
  historyLoadError,
  notesLoadError,
  pendingSupportLoadError,
  isRefreshingTab,
  setLatestPendingSupportPayload,
  clearSelectedConversationContext,
  prependNote,
  carregarMensagens,
  carregarHistorico,
  carregarHistoricoDaConversa,
  carregarLatestPendingSupport,
  carregarVisitas,
  carregarNotas,
  refreshTab,
  carregar,
} = useLeadHistoryData({
  onHistoryPayloadApplied: () => prepareQualificationAnswerDrafts(),
});

const showBlockModal = ref(false);
const showUnblockModal = ref(false);
const showOrigemModal = ref(false);
const showFeedbackModal = ref(false);
const showFeedbackDrawer = ref(false);
const showNewNoteModal = ref(false);
const showDiscardNoteModal = ref(false);
const showAlertsDrawer = ref(false);
const isCompactViewport = ref(true);
const blockReason = ref('');
const blockDetails = ref('');
const unblockReason = ref('');
const feedbackComment = ref('');
const feedbackRating = ref('');
const newNoteContent = ref('');
const pendingSupportReason = ref('');
const isUpdatingBlockStatus = ref(false);
const isSubmittingFeedback = ref(false);
const isCreatingNote = ref(false);
const isUpdatingPendingSupport = ref(false);
const historyTabId = id => `${HISTORY_TABS_ID}-tab-${id}`;
const historyPanelId = id => `${HISTORY_TABS_ID}-panel-${id}`;
const notificationDispatchGuard = ref({ ready: false, checks: [] });
const qualificationAnswerDrafts = ref({});
const savingQualificationAnswerId = ref(null);
let compactViewportMediaQuery = null;

const HISTORY_TAB_VALUES = HISTORY_TABS.map(tab => tab.id);
const normalizedHistoryTab = value =>
  HISTORY_TAB_VALUES.includes(routeString(value))
    ? routeString(value)
    : 'overview';
const activeTab = ref(normalizedHistoryTab(route.query?.tab));
const normalizedTimelineFilter = value => {
  const filter = routeString(value);
  return filter && filter !== 'all' ? filter : 'todos';
};
const activeTimelineFilter = ref(
  normalizedTimelineFilter(route.query?.event || route.query?.timeline_type)
);

const feedbackSuccessMessage = FEEDBACK_SUCCESS_MESSAGE;
const canEvaluateConversation = computed(
  () =>
    Boolean(selectedConversationId.value) && !isLoadingConversationHistory.value
);
const originDrawerSide = computed(() =>
  isCompactViewport.value ? 'bottom' : 'right'
);

const activeBuyerProfile = computed(
  () => buyerProfile.value || lead.value.buyer_profile || null
);
const leadBusinessContext = computed(() =>
  activeBusinessContextFromLead(lead.value)
);

const crmStats = computed(() => ({
  stage:
    crmSummary.value.attendance_stage_label ||
    lead.value.attendance_stage_label ||
    'Sem estágio',
  visits:
    crmSummary.value.finished_visits_count ??
    lead.value.finished_visits_count ??
    visitas.value.filter(v => v.status === 'realizada').length,
  scheduled:
    crmSummary.value.scheduled_visits_count ??
    visitas.value.filter(v => ['agendada', 'confirmada'].includes(v.status))
      .length,
  offers: crmSummary.value.offers_count ?? offers.value.length,
  closed:
    crmSummary.value.closed_deals_count ??
    crmSummary.value.signed_contracts_count ??
    offers.value.filter(offer =>
      ['accepted', 'signed', 'won'].includes(String(offer.status || ''))
    ).length,
  favorites: crmSummary.value.favorites_count ?? favorites.value.length,
  recommendations:
    crmSummary.value.recommendations_count ??
    recommendations.value.reduce(
      (total, item) => total + (item.items_count || 0),
      0
    ),
}));

const recommendedItems = computed(() =>
  recommendations.value.flatMap(item => item.items || []).slice(0, 6)
);

const currentOwnership = computed(
  () => crmOperations.value.current_ownership || null
);
const ownershipHistory = computed(() =>
  Array.isArray(crmOperations.value.ownership_history)
    ? crmOperations.value.ownership_history
    : []
);
const onlineDutyEntries = computed(() =>
  Array.isArray(crmOperations.value.online_duty)
    ? crmOperations.value.online_duty
    : []
);
const currentAccountPermissionTokens = computed(() =>
  getPermissionTokensForAccount(currentAccount.value || {})
);
const canUpdatePendingSupport = computed(() =>
  hasPermissions(
    SUPPORT_UPDATE_PERMISSIONS,
    currentAccountPermissionTokens.value
  )
);
const pendingSupportStatus = computed(() =>
  supportStatus(latestPendingSupport.value)
);
const pendingSupportStatusMeta = computed(() =>
  supportStatusMeta(latestPendingSupport.value)
);
const pendingSupportQuestionLabel = computed(() =>
  normalizeSupportQuestion(latestPendingSupport.value)
);
const pendingSupportQuestionAnswer = computed(
  () => latestPendingSupport.value?.duty?.support_question?.answer || ''
);
const pendingSupportOriginLabel = computed(() =>
  normalizeSupportOrigin(latestPendingSupport.value)
);
const pendingSupportNextAction = computed(() =>
  supportNextActionLabel(
    latestPendingSupport.value,
    canUpdatePendingSupport.value
  )
);
const shouldShowPendingSupportCard = computed(
  () => Boolean(latestPendingSupport.value) || pendingSupportLoadError.value
);
const latestPendingSupportEntry = computed(() => {
  const support = latestPendingSupport.value;
  if (!support) return null;

  return {
    id: support.id,
    status: pendingSupportStatus.value,
    status_label: pendingSupportStatusMeta.value.label,
    assignee: support.assignee,
  };
});
const visibleOnlineDutyEntries = computed(() => {
  const supportEntry = latestPendingSupportEntry.value;
  if (!supportEntry) return onlineDutyEntries.value;

  const hasSameEntry = onlineDutyEntries.value.some(
    entry => String(entry.id) === String(supportEntry.id)
  );

  return hasSameEntry
    ? onlineDutyEntries.value
    : [supportEntry, ...onlineDutyEntries.value];
});
const referralEntries = computed(() =>
  Array.isArray(crmOperations.value.referrals)
    ? crmOperations.value.referrals
    : []
);
const crmTaskEntries = computed(() =>
  Array.isArray(crmOperations.value.tasks) ? crmOperations.value.tasks : []
);
const openCrmTasks = computed(() =>
  crmTaskEntries.value.filter(task =>
    ['open', 'in_progress', 'waiting'].includes(task.status)
  )
);
const slaAlerts = computed(() =>
  Array.isArray(crmOperations.value.sla_alerts)
    ? crmOperations.value.sla_alerts
    : []
);
const firstSlaAlert = computed(
  () =>
    slaAlerts.value.find(alert => alert.severity === 'violated') ||
    slaAlerts.value[0] ||
    null
);
const hasSlaAlerts = computed(() => slaAlerts.value.length > 0);
const currentOnlineDuty = computed(
  () =>
    visibleOnlineDutyEntries.value.find(entry =>
      ['pending', 'in_progress'].includes(entry.status)
    ) ||
    visibleOnlineDutyEntries.value[0] ||
    null
);
const operationalCounts = computed(() => ({
  ownerships: ownershipHistory.value.length,
  plantao: visibleOnlineDutyEntries.value.length,
  repasses: referralEntries.value.length,
  tarefas: crmTaskEntries.value.length,
  tarefasAbertas: openCrmTasks.value.length,
  sla: slaAlerts.value.length,
}));
const operationalOwnerLabel = computed(
  () => currentOwnership.value?.agent?.name || 'Sem dono atual'
);
const operationalDutyLabel = computed(
  () => currentOnlineDuty.value?.status_label || 'Sem plantão ativo'
);
const slaActionWarning = computed(() => {
  if (!firstSlaAlert.value) return '';

  return `${firstSlaAlert.value.status_label}: ${firstSlaAlert.value.message}`;
});
const latestPendingSupportTimelineEvent = computed(() => {
  const support = latestPendingSupport.value;
  if (!support) return null;

  return {
    type: 'online_duty',
    record_id: support.id,
    label: `Plantão ${pendingSupportStatusMeta.value.label}`,
    description: [
      pendingSupportQuestionLabel.value,
      pendingSupportQuestionAnswer.value
        ? `Resposta: ${pendingSupportQuestionAnswer.value}`
        : null,
      `Origem: ${pendingSupportOriginLabel.value}`,
      `Próxima ação: ${pendingSupportNextAction.value}`,
    ]
      .filter(Boolean)
      .join(' · '),
    occurred_at:
      support.duty?.completed_at ||
      support.updated_at ||
      support.created_at ||
      support.waiting_since,
    metadata: {
      status: pendingSupportStatus.value,
      status_label: pendingSupportStatusMeta.value.label,
      assignee: support.assignee,
      support_origin: pendingSupportOriginLabel.value,
      next_action: pendingSupportNextAction.value,
    },
  };
});
const timelineWithPendingSupport = computed(() => {
  const supportEvent = latestPendingSupportTimelineEvent.value;
  if (!supportEvent) return timeline.value;

  const hasSameEvent = timeline.value.some(
    event =>
      event.type === 'online_duty' &&
      String(event.record_id) === String(supportEvent.record_id)
  );

  return hasSameEvent ? timeline.value : [supportEvent, ...timeline.value];
});
const timelineFilterBaseOptions = computed(() => {
  const operational = Array.isArray(crmOperations.value.filter_types)
    ? crmOperations.value.filter_types
    : [];
  const operationalOptions = OPERATIONAL_TIMELINE_TYPES.map(type => {
    const payloadOption = operational.find(option => option.id === type) || {};
    const timelineCount = timelineWithPendingSupport.value.filter(
      event => event.type === type
    ).length;

    return {
      id: type,
      label: payloadOption.label || TIMELINE_TYPE_META[type]?.label || type,
      count: Math.max(Number(payloadOption.count) || 0, timelineCount),
    };
  })
    .filter(option => option.count > 0)
    .map(option => ({
      id: option.id,
      label: option.label,
      count: option.count,
    }));

  const knownTypes = new Set([
    ...timelineWithPendingSupport.value
      .map(event => event.type)
      .filter(Boolean),
    ...operationalOptions.map(option => option.id),
  ]);
  const extraOptions = Array.from(knownTypes)
    .filter(type => !OPERATIONAL_TIMELINE_TYPES.includes(type))
    .map(type => ({
      id: type,
      label: TIMELINE_TYPE_META[type]?.label || type,
      count: timelineWithPendingSupport.value.filter(
        event => event.type === type
      ).length,
    }))
    .filter(option => option.count > 0);

  return [...operationalOptions, ...extraOptions];
});
const timelineFilterOptions = computed(() => [
  {
    id: 'todos',
    label: 'Todos',
    count: timelineWithPendingSupport.value.length,
  },
  {
    id: 'crm',
    label: 'CRM operacional',
    count: timelineWithPendingSupport.value.filter(event =>
      OPERATIONAL_TIMELINE_TYPES.includes(event.type)
    ).length,
  },
  ...timelineFilterBaseOptions.value,
]);
const filteredTimeline = computed(() => {
  if (activeTimelineFilter.value === 'todos') {
    return timelineWithPendingSupport.value;
  }
  if (activeTimelineFilter.value === 'crm') {
    return timelineWithPendingSupport.value.filter(event =>
      OPERATIONAL_TIMELINE_TYPES.includes(event.type)
    );
  }

  return timelineWithPendingSupport.value.filter(
    event => event.type === activeTimelineFilter.value
  );
});
const recentTimeline = computed(() => filteredTimeline.value.slice(0, 30));

const selectedConversation = computed(
  () =>
    conversations.value.find(
      conversation =>
        String(conversation.id) === String(selectedConversationId.value)
    ) ||
    selectedConversationPayload.value ||
    null
);

const selectedConversationChannelType = computed(
  () =>
    selectedConversation.value?.inbox_channel_type ||
    selectedConversation.value?.channel_type ||
    selectedConversation.value?.inbox?.channel_type ||
    selectedConversation.value?.inbox?.channel?.type ||
    selectedConversation.value?.inbox_name ||
    ''
);

const originContact = computed(() => ({
  ...lead.value,
  additional_attributes: {
    ...(lead.value.custom_attributes || {}),
    ...(lead.value.additional_attributes || {}),
    ...(historyOriginPayload.value || {}),
  },
}));

const resolvedOrigin = computed(() =>
  resolveContactOrigin(
    originContact.value,
    selectedConversationChannelType.value
  )
);

const origemRows = computed(() =>
  [
    {
      label: 'Canal',
      value:
        resolvedOrigin.value.canal || resolvedOrigin.value.origem
          ? resolvedOrigin.value.canalLabel
          : '',
      helper: resolvedOrigin.value.inferred ? 'Inferido pelo inbox' : '',
    },
    { label: 'Fonte', value: resolvedOrigin.value.origem },
    { label: 'Mídia', value: resolvedOrigin.value.midia },
    { label: 'Campanha', value: resolvedOrigin.value.campanha },
    {
      label: 'Primeiro contato',
      value: formatDateCompact(resolvedOrigin.value.firstTouch),
    },
    {
      label: 'Modalidade de contato',
      value: resolvedOrigin.value.canal ? resolvedOrigin.value.canalLabel : '',
    },
  ].filter(row => row.value)
);

const origemHasData = computed(() => origemRows.value.length > 0);

const uniquePropertyCount = computed(() => {
  const ids = new Set();
  const addId = value => {
    if (value != null && value !== '') ids.add(String(value));
  };

  recommendedItems.value.forEach(item => addId(item.id));
  favorites.value.forEach(item => addId(item.imovel?.id || item.imovel_id));
  visitas.value.forEach(item => addId(item.imovel?.id || item.imovel_id));
  offers.value.forEach(item => addId(item.imovel?.id || item.imovel_id));
  addId(publicVisitRequest.value?.imovel_id);

  return ids.size || (origemHasData.value ? 1 : 0);
});

const originChannelCount = computed(() => {
  const values = [
    resolvedOrigin.value.canal || resolvedOrigin.value.origem
      ? resolvedOrigin.value.canalLabel
      : '',
    resolvedOrigin.value.origem,
    resolvedOrigin.value.midia,
    resolvedOrigin.value.campanha,
  ]
    .map(value => String(value || '').trim())
    .filter(Boolean);

  return new Set(values).size;
});

const origemResumo = computed(() => {
  if (!origemHasData.value) return '';

  const propertyCount = uniquePropertyCount.value || 1;
  const propertyLabel = propertyCount === 1 ? 'imóvel' : 'imóveis';
  const extraChannels = Math.max(originChannelCount.value - 1, 0);
  const channelLabel =
    resolvedOrigin.value.canal || resolvedOrigin.value.origem
      ? resolvedOrigin.value.canalLabel
      : resolvedOrigin.value.midia || resolvedOrigin.value.campanha || 'Origem';
  const extraText = extraChannels
    ? ` e outros ${extraChannels} ${extraChannels === 1 ? 'canal' : 'canais'}`
    : '';

  return `Chegou por ${propertyCount} ${propertyLabel} via ${channelLabel}${extraText}.`;
});

prepareQualificationAnswerDrafts = () => {
  qualificationAnswerDrafts.value = qualificationQuestionItemsFromLead(
    lead.value
  ).reduce(
    (memo, question) => ({
      ...memo,
      [question.id]: qualificationAnswerText(question),
    }),
    {}
  );
};

const updateQualificationAnswerDraft = ({ id, value }) => {
  qualificationAnswerDrafts.value = {
    ...qualificationAnswerDrafts.value,
    [id]: value,
  };
};

const salvarQualificationAnswer = async question => {
  const answerText = qualificationAnswerDrafts.value[question.id]?.trim();
  if (!answerText) {
    useAlert('Informe a resposta antes de salvar.');
    return;
  }

  savingQualificationAnswerId.value = question.id;
  try {
    const id = route.params.id;
    const { data } = await LeadHistoryAPI.createQualificationAnswer(id, {
      question_id: question.id,
      answer_text: answerText,
      answer_value: { value: answerText },
      source: 'manual',
      confidence: 1,
    });
    lead.value = {
      ...lead.value,
      qualification_questions: data.data,
    };
    prepareQualificationAnswerDrafts();
    useAlert('Resposta salva.');
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || 'Não foi possível salvar a resposta.'
    );
  } finally {
    savingQualificationAnswerId.value = null;
  }
};

const warnSlaBeforeAction = action => {
  if (!slaActionWarning.value) return;

  useAlert(`${slaActionWarning.value}. Revise o SLA antes de ${action}.`);
};

const openPendingSupportConversation = () => {
  const support = latestPendingSupport.value;
  if (!support?.id) {
    useAlert('Não há conversa de plantão para abrir.');
    return;
  }

  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: support.id,
    },
  });
};

const updatePendingSupportStatus = async status => {
  const support = latestPendingSupport.value;
  const reason = pendingSupportReason.value.trim();

  if (!support?.id) {
    useAlert('Não há suporte pendente para atualizar.');
    return;
  }
  if (!canUpdatePendingSupport.value) {
    useAlert('Seu usuário não possui permissão para atualizar este plantão.');
    return;
  }
  if (!reason) {
    useAlert('Informe o motivo da atualização do plantão.');
    return;
  }

  isUpdatingPendingSupport.value = true;
  try {
    const { data } = await OnlineDutyAPI.updateSupportStatus(support.id, {
      status,
      agentComment: reason,
    });
    setLatestPendingSupportPayload(pendingSupportPayloadFromResponse(data));
    pendingSupportReason.value = '';
    await Promise.allSettled([
      carregarHistorico({
        conversationId: selectedConversationId.value,
        preserveMissing: true,
        alertOnError: false,
      }),
      carregarNotas({ alertOnError: false }),
    ]);
    useAlert(
      status === 'complete'
        ? 'Plantão resolvido com motivo registrado.'
        : 'Plantão atualizado com motivo registrado.'
    );
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        error.response?.data?.error ||
        'Não foi possível atualizar o plantão.'
    );
  } finally {
    isUpdatingPendingSupport.value = false;
  }
};

const submitPendingSupportUpdate = () =>
  updatePendingSupportStatus(pendingSupportNextStatus.value);

const resolvePendingSupport = () => updatePendingSupportStatus('complete');

const abrirConversaAtiva = () => {
  const conversation = conversations.value[0];
  if (!conversation) {
    useAlert('Este cliente ainda não tem conversa ativa.');
    return;
  }

  warnSlaBeforeAction('abrir a conversa');

  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: conversation.id,
    },
  });
};

const leadPhone = computed(() =>
  String(lead.value.phone_number || lead.value.phoneNumber || '').replace(
    /\D+/g,
    ''
  )
);

const ligarCliente = () => {
  if (!leadPhone.value) {
    useAlert('Este cliente não possui telefone cadastrado.');
    return;
  }
  warnSlaBeforeAction('ligar para o cliente');
  const number = leadPhone.value.startsWith('55')
    ? leadPhone.value
    : `55${leadPhone.value}`;
  window.location.href = `tel:+${number}`;
};

const criarNovaVisita = () => {
  warnSlaBeforeAction('registrar a visita');
  router.push({
    name: 'visita_new',
    params: { accountId: accountId.value },
    query: { contact_id: String(route.params.id) },
  });
};

const copyTextToClipboard = async text => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const input = document.createElement('textarea');
  input.value = text;
  input.setAttribute('readonly', 'true');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } finally {
    document.body.removeChild(input);
  }

  if (!copied) throw new Error('clipboard_copy_failed');
};

const copiarLinkHistorico = async () => {
  try {
    if (typeof window === 'undefined') throw new Error('window_unavailable');

    await copyTextToClipboard(window.location.href);
    useAlert('Link copiado com sucesso.');
  } catch {
    useAlert('Não foi possível copiar o link.');
  }
};

const whatsappNotificationGuard = computed(() => {
  if (!notificationDispatchGuard.value?.ready) return null;

  return notificationDispatchGuard.value.checks?.find(
    check => check.id === 'whatsapp'
  );
});

const updateNotificationDispatchGuard = guard => {
  notificationDispatchGuard.value = guard || { ready: false, checks: [] };
};

const refreshLeadDemandContext = async () => {
  await carregarHistorico({
    conversationId: selectedConversationId.value,
    preserveMissing: true,
    alertOnError: false,
  });
};

const enviarMensagemCliente = () => {
  if (!leadPhone.value) {
    useAlert('Telefone não informado para este cliente.');
    return;
  }
  if (lead.value.blocked) {
    useAlert(
      'Contato bloqueado: desbloqueie e registre o motivo antes de enviar WhatsApp.'
    );
    return;
  }
  if (
    whatsappNotificationGuard.value &&
    !whatsappNotificationGuard.value.allowed
  ) {
    useAlert(whatsappNotificationGuard.value.message);
    return;
  }
  warnSlaBeforeAction('enviar WhatsApp');
  const messageWindow = window.open(
    `https://wa.me/55${leadPhone.value.replace(/^55/, '')}`,
    '_blank',
    'noopener,noreferrer'
  );
  if (!messageWindow) {
    useAlert(
      'Não foi possível abrir o WhatsApp. Verifique o bloqueador de pop-ups.'
    );
  }
};

const toggleOrigem = () => {
  const shouldOpen = !showOrigemModal.value;
  if (shouldOpen) {
    trackCrmEvent({
      accountId: accountId.value,
      name: 'lais_conversation_origin_modal_opened',
      properties: {
        conversation_id: selectedConversationId.value,
        contact_id: route.params.id,
        canal: resolvedOrigin.value.canal || '',
        origem: resolvedOrigin.value.origem || '',
        campanha: resolvedOrigin.value.campanha || '',
      },
    });
  }

  showOrigemModal.value = true;
};

const openFeedbackDialog = () => {
  if (!selectedConversationId.value) {
    useAlert('Selecione uma conversa antes de avaliar o atendimento.');
    return;
  }
  if (isLoadingConversationHistory.value) {
    useAlert('Aguarde o carregamento da conversa antes de avaliar.');
    return;
  }

  feedbackComment.value = conversationFeedback.value?.comment || '';
  feedbackRating.value = conversationFeedback.value?.rating || '';

  if (isCompactViewport.value) {
    showFeedbackDrawer.value = true;
    return;
  }

  showFeedbackModal.value = true;
};

const closeFeedbackDialog = () => {
  showFeedbackModal.value = false;
  showFeedbackDrawer.value = false;
};

const enviarFeedback = async () => {
  if (isSubmittingFeedback.value) return;

  if (!selectedConversationId.value) {
    useAlert('Selecione uma conversa antes de enviar a avaliação.');
    return;
  }

  if (isLoadingConversationHistory.value) {
    useAlert('Aguarde o carregamento da conversa antes de enviar a avaliação.');
    return;
  }

  const comment = feedbackComment.value.trim();
  if (!comment) {
    useAlert('Informe um comentário antes de enviar.');
    return;
  }

  isSubmittingFeedback.value = true;
  try {
    const { data } = await LeadHistoryAPI.submitFeedback({
      contact_id: route.params.id,
      conversation_id: selectedConversationId.value,
      comment,
      rating: feedbackRating.value || null,
    });
    conversationFeedback.value = data.payload;
    closeFeedbackDialog();
    trackCrmEvent({
      accountId: accountId.value,
      name: 'lais_conversation_feedback_submitted',
      properties: {
        conversation_id: selectedConversationId.value,
        contact_id: route.params.id,
        rating: feedbackRating.value || null,
      },
    });
    useAlert(feedbackSuccessMessage);
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível enviar sua avaliação.'
    );
  } finally {
    isSubmittingFeedback.value = false;
  }
};

const openNewNoteModal = () => {
  newNoteContent.value = '';
  showNewNoteModal.value = true;
};

const resetNewNoteModal = () => {
  showNewNoteModal.value = false;
  showDiscardNoteModal.value = false;
  newNoteContent.value = '';
};

const closeNewNoteModal = () => {
  if (isCreatingNote.value) return;

  if (newNoteContent.value.trim()) {
    showDiscardNoteModal.value = true;
    return;
  }
  resetNewNoteModal();
};

const discardNewNote = () => {
  if (isCreatingNote.value) return;

  resetNewNoteModal();
};

const salvarNovaAnotacao = async () => {
  if (isCreatingNote.value) return;

  const content = newNoteContent.value.trim();
  if (!content) {
    useAlert('Escreva uma anotação antes de salvar.');
    return;
  }

  isCreatingNote.value = true;
  try {
    const { data } = await LeadHistoryAPI.createNote(route.params.id, content);
    prependNote(data);
    resetNewNoteModal();
    useAlert('Anotação salva.');
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível salvar a anotação.'
    );
  } finally {
    isCreatingNote.value = false;
  }
};

useEscClose(showBlockModal, () => {
  showBlockModal.value = false;
});
useEscClose(showUnblockModal, () => {
  showUnblockModal.value = false;
});
useEscClose(showOrigemModal, () => {
  showOrigemModal.value = false;
});
useEscClose(showFeedbackModal, () => {
  showFeedbackModal.value = false;
});
useEscClose(showFeedbackDrawer, () => {
  showFeedbackDrawer.value = false;
});
useEscClose(showNewNoteModal, () => {
  closeNewNoteModal();
});

const abrirSaberMais = () => {
  if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
    window.Intercom('showNewMessage', 'Saber mais sobre Lais Visita');
  } else {
    window.open('/lais/suporte', '_blank', 'noopener');
  }
};

const voltarPaginaAnterior = () => {
  if (typeof window !== 'undefined' && window.history.state?.back) {
    router.back();
    return;
  }

  router.push({
    name: 'lais_leads_index',
    params: { accountId: accountId.value },
  });
};

const gerenciarBairros = () => {
  if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
    window.Intercom(
      'showNewMessage',
      'Quero gerenciar os bairros e cidades de atuação da minha imobiliária na Lais.'
    );
  } else {
    window.open('/lais/suporte', '_blank', 'noopener');
  }
};

const abrirFavoritosDoLead = () => {
  router.push({
    name: 'favoritos_index',
    params: { accountId: accountId.value },
    query: { contact_id: String(route.params.id) },
  });
};

const openBlockModal = () => {
  blockReason.value = '';
  blockDetails.value = '';
  showBlockModal.value = true;
};

const openUnblockModal = () => {
  unblockReason.value = '';
  showUnblockModal.value = true;
};

const openCrmTimeline = () => {
  activeTab.value = 'historico';
  activeTimelineFilter.value = 'crm';
};

const retryConversationLoad = () => {
  if (selectedConversationId.value) {
    return carregarMensagens(selectedConversationId.value);
  }

  return carregar();
};

const syncHistoryRouteState = updates => {
  const nextQuery = compactQuery({ ...route.query, ...updates });
  if (querySignature(route.query) === querySignature(nextQuery)) return;

  router.replace({ query: nextQuery }).catch(() => {});
};

const syncConversationRouteState = conversationId => {
  syncHistoryRouteState({
    conversation_id: conversationId ? String(conversationId) : undefined,
  });
};

watch(selectedConversationId, conversationId => {
  syncConversationRouteState(conversationId);
  if (!isLoading.value) clearSelectedConversationContext();
  carregarMensagens(conversationId);
  carregarHistoricoDaConversa(conversationId);
});

watch(
  () => route.query.conversation_id || route.query.conversation,
  value => {
    const requestedConversationId = routeString(value);
    const fallbackConversationId = conversations.value[0]?.id || null;

    if (!requestedConversationId) {
      if (
        String(selectedConversationId.value || '') !==
        String(fallbackConversationId || '')
      ) {
        selectedConversationId.value = fallbackConversationId;
      } else {
        syncConversationRouteState(fallbackConversationId);
      }
      return;
    }

    const conversation = conversations.value.find(
      item => String(item.id) === String(requestedConversationId)
    );
    if (
      conversation &&
      String(selectedConversationId.value) !== String(conversation.id)
    ) {
      selectedConversationId.value = conversation.id;
    } else if (!conversation) {
      if (
        String(selectedConversationId.value || '') !==
        String(fallbackConversationId || '')
      ) {
        selectedConversationId.value = fallbackConversationId;
      } else {
        syncConversationRouteState(fallbackConversationId);
      }
    }
  }
);

watch(timelineFilterOptions, options => {
  if (!options.some(option => option.id === activeTimelineFilter.value)) {
    activeTimelineFilter.value = 'todos';
  }
});

const syncCompactViewport = event => {
  isCompactViewport.value = Boolean(event.matches);

  if (isCompactViewport.value && showFeedbackModal.value) {
    showFeedbackModal.value = false;
    showFeedbackDrawer.value = true;
  } else if (!isCompactViewport.value && showFeedbackDrawer.value) {
    showFeedbackDrawer.value = false;
    showFeedbackModal.value = true;
  }
};

watch(
  () => route.params.id,
  (contactId, previousContactId) => {
    if (!contactId || contactId === previousContactId) return;

    activeTab.value = normalizedHistoryTab(route.query?.tab);
    activeTimelineFilter.value = normalizedTimelineFilter(
      route.query?.event || route.query?.timeline_type
    );
    carregar();
  }
);

watch(activeTab, tab => {
  syncHistoryRouteState({ tab: tab === 'overview' ? undefined : tab });
  refreshTab(tab);
});

watch(activeTimelineFilter, filter => {
  syncHistoryRouteState({
    event: filter === 'todos' ? undefined : filter,
    tab: activeTab.value === 'historico' ? 'historico' : route.query.tab,
  });
});

watch(
  () => route.query.tab,
  value => {
    const next = normalizedHistoryTab(value);
    if (activeTab.value !== next) activeTab.value = next;
  }
);

watch(
  () => route.query.event || route.query.timeline_type,
  value => {
    const next = normalizedTimelineFilter(value);
    if (activeTimelineFilter.value !== next) activeTimelineFilter.value = next;
  }
);

const confirmarBloqueio = async () => {
  if (!blockReason.value) {
    useAlert('Selecione uma razão para o bloqueio');
    return;
  }

  isUpdatingBlockStatus.value = true;
  try {
    const id = route.params.id;
    const { data } = await BlockedContactsAPI.create({
      contact_id: id,
      motivo: blockReason.value,
      observacoes: blockDetails.value?.trim() || '',
      source: 'lead_profile',
    });
    lead.value.blocked = true;
    lead.value.blocked_contact = blockedContactPayload(data.payload);
    showBlockModal.value = false;
    useAlert('Número bloqueado com sucesso');
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível bloquear este contato.'
    );
  } finally {
    isUpdatingBlockStatus.value = false;
  }
};

const confirmarDesbloqueio = async () => {
  if (!unblockReason.value.trim()) {
    useAlert('Informe o motivo do desbloqueio');
    return;
  }

  isUpdatingBlockStatus.value = true;
  try {
    const id = route.params.id;
    if (lead.value.blocked_contact?.id) {
      await BlockedContactsAPI.destroy(lead.value.blocked_contact.id, {
        motivo_desbloqueio: unblockReason.value.trim(),
      });
    } else {
      await LeadHistoryAPI.updateContact(id, { blocked: false });
    }
    lead.value.blocked = false;
    lead.value.blocked_contact = null;
    showUnblockModal.value = false;
    unblockReason.value = '';
    useAlert('Número desbloqueado com sucesso');
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        'Não foi possível desbloquear este contato.'
    );
  } finally {
    isUpdatingBlockStatus.value = false;
  }
};

onMounted(() => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    compactViewportMediaQuery = window.matchMedia('(max-width: 1023px)');
    syncCompactViewport(compactViewportMediaQuery);
    if (compactViewportMediaQuery.addEventListener) {
      compactViewportMediaQuery.addEventListener('change', syncCompactViewport);
    } else if (compactViewportMediaQuery.addListener) {
      compactViewportMediaQuery.addListener(syncCompactViewport);
    }
  }

  carregar();
});

onBeforeUnmount(() => {
  if (!compactViewportMediaQuery) return;

  if (compactViewportMediaQuery.removeEventListener) {
    compactViewportMediaQuery.removeEventListener(
      'change',
      syncCompactViewport
    );
  } else if (compactViewportMediaQuery.removeListener) {
    compactViewportMediaQuery.removeListener(syncCompactViewport);
  }
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-white font-redHatText lg:overflow-hidden"
  >
    <LaisHeader
      title="Histórico de conversa"
      helper="Entenda o contexto para prover um atendimento contínuo."
      :breadcrumb="[
        { label: 'Clientes', to: { name: 'lais_leads_index' } },
        { label: lead.name || 'Cliente' },
      ]"
    >
      <template #actions>
        <LeadHistoryHeaderActions
          :blocked="lead.blocked"
          @back="voltarPaginaAnterior"
          @copy-link="copiarLinkHistorico"
          @open-alerts="showAlertsDrawer = true"
          @open-favorites="abrirFavoritosDoLead"
          @block="openBlockModal"
          @unblock="openUnblockModal"
        />
      </template>
    </LaisHeader>

    <LeadBlockedBanner
      v-if="lead.blocked"
      :blocked-contact="lead.blocked_contact"
    />

    <div
      v-if="isLoading"
      class="flex-1 grid place-items-center text-lais-text-muted"
    >
      Carregando…
    </div>

    <div
      v-else
      class="grid flex-none grid-cols-1 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_360px] lg:overflow-hidden xl:grid-cols-[minmax(0,1fr)_420px]"
    >
      <!-- Coluna principal: cards de perfil + visita -->
      <div
        class="min-w-0 p-4 sm:p-6 lg:min-h-0 lg:overflow-auto lg:p-6 xl:p-10"
      >
        <p class="text-[13px] text-lais-text-muted mb-4">
          Acompanhe todos os detalhes do seu cliente.
        </p>

        <LeadProfileErrorCard
          v-if="profileLoadError"
          class="mb-5 md:max-w-2xl"
          :title="profileErrorCopy.title"
          :description="profileErrorCopy.description"
          @retry="carregar"
        />

        <div v-else class="space-y-5">
          <LeadProfileIdentityCard :lead="lead" />

          <LaisGalleryTabBar
            v-model="activeTab"
            :tabs="HISTORY_TABS"
            id-base="lead-history-tabs"
            aria-label="Histórico do cliente"
          />

          <div
            :id="historyPanelId(activeTab)"
            role="tabpanel"
            :aria-labelledby="historyTabId(activeTab)"
            tabindex="0"
            class="min-w-0 outline-none focus-visible:shadow-lais-focus"
          >
            <LeadJourney v-if="activeTab === 'overview'" :stats="crmStats" />

            <div
              class="grid gap-5 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2"
            >
              <LeadHistoryState
                v-if="activeTab === 'overview' && historyLoadError"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
                :title="historyErrorCopy.title"
                :description="historyErrorCopy.description"
                action-label="Tentar novamente"
                @action="
                  carregarHistorico({
                    conversationId: selectedConversationId,
                    alertOnError: true,
                  })
                "
              />

              <LeadProfileSummaryCard
                v-if="activeTab === 'overview'"
                :lead="lead"
                :buyer-profile="activeBuyerProfile"
                :business-context="leadBusinessContext"
                :origem-has-data="origemHasData"
                :origem-resumo="origemResumo"
                :origem-rows="origemRows"
                @toggle-origem="toggleOrigem"
              />

              <LeadPublicVisitRequestCard
                v-if="activeTab === 'overview'"
                :public-visit-request="publicVisitRequest"
                :visitas-load-error="visitasLoadError"
                :visitas-error-copy="visitasErrorCopy"
                @saber-mais="abrirSaberMais"
                @retry="carregarVisitas()"
              />

              <LeadCrmSummaryCard
                v-if="activeTab === 'overview'"
                :lead="lead"
                :crm-stats="crmStats"
                :crm-summary="crmSummary"
                :operational-counts="operationalCounts"
                :operational-owner-label="operationalOwnerLabel"
                :operational-duty-label="operationalDutyLabel"
                :has-sla-alerts="hasSlaAlerts"
                :sla-action-warning="slaActionWarning"
                :answer-drafts="qualificationAnswerDrafts"
                :saving-answer-id="savingQualificationAnswerId"
                @view-timeline="openCrmTimeline"
                @update:draft="updateQualificationAnswerDraft"
                @save-answer="salvarQualificationAnswer"
                @message="abrirConversaAtiva"
                @call="ligarCliente"
                @new-visit="criarNovaVisita"
              />

              <LeadPendingSupportCard
                v-if="activeTab === 'overview' && shouldShowPendingSupportCard"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
                :support="latestPendingSupport"
                :load-error="pendingSupportLoadError"
                :can-update="canUpdatePendingSupport"
                :is-updating="isUpdatingPendingSupport"
                :reason="pendingSupportReason"
                :next-status="pendingSupportNextStatus"
                @retry="carregarLatestPendingSupport({ alertOnError: true })"
                @update:reason="pendingSupportReason = $event"
                @update:next-status="pendingSupportNextStatus = $event"
                @submit="submitPendingSupportUpdate"
                @resolve="resolvePendingSupport"
                @open-conversation="openPendingSupportConversation"
              />

              <LaisCard
                v-if="activeTab === 'overview'"
                title="Contatos do cliente"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
              >
                <LeadContactCards
                  :lead="lead"
                  @message="enviarMensagemCliente"
                  @call="ligarCliente"
                  @profile="copiarLinkHistorico"
                />
              </LaisCard>

              <LaisCard
                v-if="activeTab === 'overview'"
                title="Preferências de notificação"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
              >
                <ContactNotificationPreferences
                  :contact="lead"
                  :contact-id="route.params.id"
                  initial-purpose="NOTIMANAGER"
                  @dispatch-guard-change="updateNotificationDispatchGuard"
                />
              </LaisCard>

              <LeadDemandContextPanel
                v-if="activeTab === 'overview'"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
                :contact="lead"
                :contact-id="route.params.id"
                :notification-guard="notificationDispatchGuard"
                @changed="refreshLeadDemandContext"
              />

              <LeadFeedbackCard
                v-if="activeTab === 'overview'"
                :feedback="conversationFeedback"
                :can-evaluate="canEvaluateConversation"
                :unavailable-message="
                  selectedConversationId
                    ? 'Carregando dados da conversa selecionada.'
                    : 'Selecione uma conversa para registrar a avaliação.'
                "
                @open="openFeedbackDialog"
              />

              <LeadBuyerProfileCard
                v-if="activeTab === 'overview'"
                :profile="activeBuyerProfile"
              />

              <LeadRecommendationsCard
                v-if="activeTab === 'imoveis'"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
                :items="recommendedItems"
                :history-load-error="historyLoadError"
                :history-error-copy="historyErrorCopy"
                :is-refreshing="isRefreshingTab('imoveis')"
                @refresh="refreshTab('imoveis')"
              />

              <LeadStageTimelinePanel
                v-if="activeTab === 'overview'"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
                :contact-id="route.params.id"
                :conversation-id="selectedConversationId"
              />

              <LeadHistoryTimelineCard
                v-if="activeTab === 'historico'"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
                :events="recentTimeline"
                :filter-options="timelineFilterOptions"
                :active-filter="activeTimelineFilter"
                :history-load-error="historyLoadError"
                :history-error-copy="historyErrorCopy"
                :is-refreshing="isRefreshingTab('historico')"
                @refresh="refreshTab('historico')"
                @update:active-filter="activeTimelineFilter = $event"
              />

              <LeadOperationAreasCard
                v-if="activeTab === 'overview'"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
                :lead="lead"
                @manage="gerenciarBairros"
              />

              <LeadNotesCard
                v-if="activeTab === 'anotacoes'"
                class="md:col-span-2 lg:col-span-1 xl:col-span-2"
                :notes="contactNotes"
                :is-loading-notes="isLoadingNotes"
                :notes-load-error="notesLoadError"
                :notes-error-copy="notesErrorCopy"
                @refresh="carregarNotas({ alertOnError: false })"
                @retry="carregarNotas()"
                @new-note="openNewNoteModal"
                @new-visit="criarNovaVisita"
              />
            </div>

            <LeadScheduledVisitsCard
              v-if="!profileLoadError && activeTab === 'historico'"
              class="mt-5"
              :visitas="visitas"
              :visitas-load-error="visitasLoadError"
              :visitas-error-copy="visitasErrorCopy"
              @retry="carregarVisitas()"
            />
          </div>
        </div>
      </div>

      <!-- Drawer mensagens -->
      <LeadConversationPanel
        class="min-h-[28rem] lg:min-h-0"
        :conversations="conversations"
        :selected-conversation-id="selectedConversationId"
        :messages="messages"
        :is-loading-messages="isLoadingMessages"
        :conversation-load-error="conversationLoadError"
        :conversation-error-copy="conversationErrorCopy"
        :lead-name="lead.name"
        @select="selectedConversationId = $event"
        @retry="retryConversationLoad"
      />
    </div>

    <LeadOriginDrawer
      :show="showOrigemModal"
      :side="originDrawerSide"
      :origem-has-data="origemHasData"
      :origem-resumo="origemResumo"
      :origem-rows="origemRows"
      @close="showOrigemModal = false"
    />

    <LeadFeedbackDialogs
      :show-modal="showFeedbackModal"
      :show-drawer="showFeedbackDrawer"
      :comment="feedbackComment"
      :rating="feedbackRating"
      :is-submitting="isSubmittingFeedback"
      @close="closeFeedbackDialog"
      @submit="enviarFeedback"
      @update:comment="feedbackComment = $event"
      @update:rating="feedbackRating = $event"
    />

    <LeadNoteDialogs
      :show-new-note="showNewNoteModal"
      :show-discard="showDiscardNoteModal"
      :content="newNoteContent"
      :is-creating="isCreatingNote"
      @close="closeNewNoteModal"
      @confirm="salvarNovaAnotacao"
      @update:content="newNoteContent = $event"
      @close-discard="showDiscardNoteModal = false"
      @discard="discardNewNote"
    />

    <LeadBlockDialogs
      :show-block="showBlockModal"
      :show-unblock="showUnblockModal"
      :block-reason="blockReason"
      :block-details="blockDetails"
      :unblock-reason="unblockReason"
      :is-updating="isUpdatingBlockStatus"
      @close-block="showBlockModal = false"
      @confirm-block="confirmarBloqueio"
      @close-unblock="showUnblockModal = false"
      @confirm-unblock="confirmarDesbloqueio"
      @update:block-reason="blockReason = $event"
      @update:block-details="blockDetails = $event"
      @update:unblock-reason="unblockReason = $event"
    />

    <AlertsDrawer
      v-if="showAlertsDrawer"
      :show="showAlertsDrawer"
      :contact-id="route.params.id"
      :lead-name="lead.name"
      @close="showAlertsDrawer = false"
    />
  </section>
</template>
